/**
 * Language Route Wrapper — syncs URL / preference with static i18n bundles.
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import languageConfig from "../../config/languageConfig";
import { initializeI18n, changeLanguage } from "../../i18n/config";

const LanguageRouteWrapper = ({ children, defaultLanguage = "en" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [i18nInitialized, setI18nInitialized] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!i18nInitialized) {
        await initializeI18n();
        setI18nInitialized(true);
      }
    };
    init();
  }, [i18nInitialized]);

  useEffect(() => {
    if (!i18nInitialized) return;

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];
    const urlCodes = languageConfig.getUrlCodes();

    let currentLanguage = defaultLanguage;
    const hasLanguagePrefix = urlCodes.includes(firstSegment);

    if (hasLanguagePrefix) {
      currentLanguage = languageConfig.getTranslationCode(firstSegment);
      if (hasRedirected) setHasRedirected(false);
    } else {
      const preferredLang = localStorage.getItem("preferredLanguage");
      currentLanguage =
        preferredLang || languageConfig.detectLanguage() || defaultLanguage;

      if (currentLanguage !== defaultLanguage && !hasRedirected) {
        const urlCode = languageConfig.getUrlCode(currentLanguage);
        const newPath = `/${urlCode}${location.pathname === "/" ? "" : location.pathname}`;
        setHasRedirected(true);
        navigate(newPath, { replace: true });
        return;
      }
    }

    let cancelled = false;
    (async () => {
      await changeLanguage(currentLanguage);
      if (cancelled) return;
      const langObj = languageConfig.getLanguage(currentLanguage);
      if (langObj && document.documentElement) {
        document.documentElement.lang =
          langObj.locale || currentLanguage;
        document.documentElement.dir =
          langObj.dir === "rtl" ? "rtl" : "ltr";
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    defaultLanguage,
    i18nInitialized,
    navigate,
    hasRedirected,
  ]);

  return <>{children}</>;
};

export default LanguageRouteWrapper;
